import { useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, FileSpreadsheet, FileText, FileType2, Filter } from 'lucide-react';
import { PARTNERSHIP_TYPES, STATUS_LABELS } from '../../lib/utils';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export async function download(fmt, params) {
  const q = new URLSearchParams(params).toString();
  const token = localStorage.getItem('aawta_token');
  const url = `${API_BASE}/api/exports/leads.${fmt}${q ? `?${q}` : ''}`;
  try {
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${resp.status}`);
    }
    const blob = await resp.blob();
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dlUrl;
    a.download = `aawta-partners-${new Date().toISOString().slice(0, 10)}.${fmt}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(dlUrl), 5000);
    toast.success(`${fmt.toUpperCase()} download started`);
  } catch (e) {
    toast.error(e.message || 'Export failed');
  }
}

const FIELD_GROUPS = [
  { label: 'Basic Info', fields: [['company_name', 'Company'], ['primary_contact', 'Contact'], ['title', 'Title'], ['contact_type', 'Contact Type']] },
  { label: 'Contact Info', fields: [['email', 'Email'], ['phone', 'Phone'], ['office_phone', 'Office Phone'], ['mobile_phone', 'Mobile Phone'], ['website', 'Website'], ['social_media_str', 'Social Media']] },
  { label: 'Address Information', fields: [['address_line1', 'Street Address 1'], ['address_line2', 'Street Address 2'], ['address_city', 'City'], ['address_state', 'State'], ['address_zip', 'ZIP/Postal Code'], ['address_county', 'County'], ['address_country', 'Country'], ['mailing_address_line1', 'Mailing Address / P.O. Box'], ['mailing_city', 'Mailing City'], ['mailing_state', 'Mailing State'], ['mailing_zip', 'Mailing Zip']] },
  { label: 'Partnership', fields: [['partnership_type', 'Partnership Type'], ['partnership_tier', 'Partnership Tier'], ['status', 'Pipeline Stage'], ['lead_source', 'Lead Source'], ['assigned_to_name', 'Assigned Team Member'], ['closure_type', 'Closure'], ['estimated_revenue', 'Opportunity Value'], ['revenue', 'Closed Revenue']] },
  { label: 'Activity & Dates', fields: [['last_contact_date', 'Last Contact'], ['next_followup_date', 'Next Follow-Up'], ['notes', 'Notes'], ['documents_count', 'Documents'], ['created_at', 'Created'], ['updated_at', 'Last Updated']] },
];
const ALL_FIELD_KEYS = FIELD_GROUPS.flatMap((g) => g.fields.map(([k]) => k));

export default function ExportLeadsMenu({ isAdmin, currentFilters = {}, selectedIds = [], users = [] }) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    date_from: '', date_to: '',
    status: '', partnership_type: '',
    partnership_tier: '', assigned_to: '',
    lead_source: '', q: '',
  });
  const [format, setFormat] = useState('xlsx');
  const [selectedFields, setSelectedFields] = useState(ALL_FIELD_KEYS);

  if (!isAdmin) return null; // Only leadership/director can export

  const cleaned = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v));

  const exportAll = (fmt) => download(fmt, {});
  const exportFiltered = (fmt) => download(fmt, cleaned(currentFilters));
  const exportSelected = (fmt) => {
    if (selectedIds.length === 0) return toast.error('No records selected');
    download(fmt, { ids: selectedIds.join(',') });
  };

  const applyCustom = () => {
    if (selectedFields.length === 0) return toast.error('Select at least one field to export');
    const params = cleaned(filters);
    if (format !== 'pdf' && selectedFields.length < ALL_FIELD_KEYS.length) params.fields = selectedFields.join(',');
    download(format, params);
    setFilterOpen(false);
  };

  const toggleField = (k) => setSelectedFields((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const toggleGroup = (g) => {
    const keys = g.fields.map(([k]) => k);
    const allOn = keys.every((k) => selectedFields.includes(k));
    setSelectedFields((s) => (allOn ? s.filter((k) => !keys.includes(k)) : [...new Set([...s, ...keys])]));
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" data-testid="export-menu-trigger">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>All Partners</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => exportAll('xlsx')} data-testid="export-all-xlsx"><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)</DropdownMenuItem>
          <DropdownMenuItem onClick={() => exportAll('csv')} data-testid="export-all-csv"><FileType2 className="w-4 h-4 mr-2" /> CSV</DropdownMenuItem>
          <DropdownMenuItem onClick={() => exportAll('pdf')} data-testid="export-all-pdf"><FileText className="w-4 h-4 mr-2" /> PDF Summary</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Current Filter View</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => exportFiltered('xlsx')} data-testid="export-filtered-xlsx"><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel of filtered</DropdownMenuItem>
          <DropdownMenuItem onClick={() => exportFiltered('csv')} data-testid="export-filtered-csv"><FileType2 className="w-4 h-4 mr-2" /> CSV of filtered</DropdownMenuItem>
          <DropdownMenuItem onClick={() => exportFiltered('pdf')} data-testid="export-filtered-pdf"><FileText className="w-4 h-4 mr-2" /> PDF of filtered</DropdownMenuItem>
          {selectedIds.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Selected ({selectedIds.length})</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => exportSelected('xlsx')} data-testid="export-selected-xlsx"><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel of selected</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportSelected('csv')} data-testid="export-selected-csv"><FileType2 className="w-4 h-4 mr-2" /> CSV of selected</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportSelected('pdf')} data-testid="export-selected-pdf"><FileText className="w-4 h-4 mr-2" /> PDF of selected</DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setFilterOpen(true)} data-testid="export-custom"><Filter className="w-4 h-4 mr-2" /> Custom filter export…</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="export-filter-dialog">
          <DialogHeader><DialogTitle>Custom Export</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">From date</Label><Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" /></div>
            <div><Label className="text-xs">To date</Label><Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" /></div>
            <div className="col-span-2">
              <Label className="text-xs">Partnership Type</Label>
              <Select value={filters.partnership_type} onValueChange={(v) => setFilters({ ...filters, partnership_type: v === '_all' ? '' : v })}>
                <SelectTrigger data-testid="filter-partnership-type"><SelectValue placeholder="All types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Types</SelectItem>
                  {PARTNERSHIP_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Pipeline Stage</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === '_all' ? '' : v })}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All stages" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Stages</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Assigned Team Member</Label>
              <Select value={filters.assigned_to} onValueChange={(v) => setFilters({ ...filters, assigned_to: v === '_all' ? '' : v })}>
                <SelectTrigger data-testid="filter-assigned-to"><SelectValue placeholder="Anyone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Anyone</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Lead Source</Label><Input value={filters.lead_source} onChange={(e) => setFilters({ ...filters, lead_source: e.target.value })} placeholder="e.g. Referral" data-testid="filter-lead-source" /></div>
            <div><Label className="text-xs">Search keyword</Label><Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Company or contact" data-testid="filter-search" /></div>
            <div className="col-span-2">
              <Label className="text-xs">Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger data-testid="filter-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="pdf">PDF Summary</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="border-t pt-3" data-testid="export-fields-section">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold">Fields to include ({selectedFields.length}/{ALL_FIELD_KEYS.length})</Label>
              <button type="button" className="text-[11px] text-magenta hover:underline" data-testid="export-fields-toggle-all"
                onClick={() => setSelectedFields(selectedFields.length === ALL_FIELD_KEYS.length ? [] : ALL_FIELD_KEYS)}>
                {selectedFields.length === ALL_FIELD_KEYS.length ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <div className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
              {FIELD_GROUPS.map((g) => (
                <div key={g.label} data-testid={`field-group-${g.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <label className="flex items-center gap-2 text-xs font-semibold text-navy cursor-pointer mb-1">
                    <input type="checkbox" className="accent-[#C2185B]"
                      checked={g.fields.every(([k]) => selectedFields.includes(k))}
                      onChange={() => toggleGroup(g)} />
                    {g.label}
                  </label>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-5">
                    {g.fields.map(([k, l]) => (
                      <label key={k} className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                        <input type="checkbox" className="accent-[#C2185B]" checked={selectedFields.includes(k)} onChange={() => toggleField(k)} data-testid={`export-field-${k}`} />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {format === 'pdf' && <p className="text-[10px] text-slate-400 mt-1.5">Field selection applies to Excel & CSV; the PDF summary uses a fixed layout.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFilterOpen(false)}>Cancel</Button>
            <Button onClick={applyCustom} className="bg-magenta hover:bg-magenta/90" data-testid="apply-export">Export</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
